import React, { useEffect, useContext, useRef, useState, Fragment } from 'react';
import FadeIn from 'react-fade-in';
import { Observer } from 'mobx-react-lite';
import { GlobalHotKeys } from 'react-hotkeys';

import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../constants/errorMessages';
import * as fn from '../../../../utilities/_functions';
import * as oh from '../../../../utilities/operationHelper';

import api from '../../../../api';

import './AddExamData.scss';

function AddExamData(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const allDatasets = useRef([]);
    const hasUnsavedChanges = useRef(false);
    const quickDrawer = useContext(QuickDrawerStore);
    const [expandedCategories, setExpandedCategories] = useState([]);
    const [selectedDatasets, setSelectedDatasets] = useState([]);
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        if (isMounted.current) {
            if (props && props.extraProps) {
                const { examPublishedTemplateId, examDatasetIds, selectedDatasets } = props.extraProps;

                api.ExamDatasetGroups.search({
                    parameters: [{
                        field: 'ExamPublishedTemplateId',
                        value: examPublishedTemplateId,
                    }],
                    includeTotalCount: false,
                    loadProperties: true,
                    limit: 1
                })
                    .then(({ data }) => {
                        if (isMounted.current) {
                            const datasets = data && data.result && data.result.length > 0 ? data.result[0].examDatasets : [];

                            if (examDatasetIds && examDatasetIds.length > 0) {
                                allDatasets.current = examDatasetIds.map(di => { return datasets.filter(da => di === da.id)[0] }).filter(di => !!di);
                            }
                            else {
                                allDatasets.current = datasets;
                            }

                            if (selectedDatasets && selectedDatasets.length > 0) {
                                const SelectedDatasets = allDatasets.current.filter(d1 => selectedDatasets.some(d2 => d1.id === d2.id));
                                setSelectedDatasets(SelectedDatasets);
                            }
                        }
                    })
                    .finally(() => {
                        if (isMounted.current) {
                            setIsReady(true);
                        }
                    })
            }
        }
        return () => {
            isMounted.current = false;
        }
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (hasUnsavedChanges.current) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleSubmit = event => {
        event.preventDefault();

        if (props.onSuccess && fn.isFunction(props.onSuccess)) {
            const sortedSelectedDatasets = selectedDatasets.sort((a, b) => {
                if (a.category === b.category) {
                    return b.label - a.label;
                }
                return a.category > b.category ? 1 : -1;
            })
            const data = {
                items: sortedSelectedDatasets,
            }
            props.onSuccess(event, { updated: true, data: data });
        }
    }

    const isAllSelected = (e) => {
        return !allDatasets.current.some(d1 => !selectedDatasets.some(d2 => d1.id === d2.id));
    }

    const handleSelectAllToggle = (e) => {
        let SelectedDatasets = [...selectedDatasets];

        if (isAllSelected()) {
            SelectedDatasets = SelectedDatasets.filter(d1 => !allDatasets.current.some(d2 => d1.id === d2.id));
        }
        else {
            SelectedDatasets = allDatasets.current.reduce((array, dataset) => {
                if (!array.some(a => a.id === dataset.id)) {
                    array.push(dataset);
                }
                return array;
            }, SelectedDatasets);
        }

        setSelectedDatasets(SelectedDatasets);
    }

    const handleCategoryToggle = (e, category) => {
        const index = expandedCategories.findIndex(c => c === category);
        let ExpandedCategories = [...expandedCategories];

        if (index < 0) {
            ExpandedCategories.push(category)
        }
        else {
            ExpandedCategories.splice(index, 1);
        }

        setExpandedCategories(ExpandedCategories);
    }

    const isAllCategorySelected = (category) => {
        const categoryDatasets = allDatasets.current.filter(d => d.category === category);
        const selectedCategoryDatasets = selectedDatasets.filter(d => d.category === category);
        return !categoryDatasets.some(d1 => !selectedCategoryDatasets.some(d2 => d1.id === d2.id));
    }

    const handleCategorySelectAllToggle = (e, category) => {
        const categoryDatasets = allDatasets.current.filter(d => d.category === category);
        let SelectedDatasets = [...selectedDatasets];

        if (isAllCategorySelected(category)) {
            SelectedDatasets = SelectedDatasets.filter(d1 => !categoryDatasets.some(d2 => d1.id === d2.id));
        }
        else {
            SelectedDatasets = categoryDatasets.reduce((array, dataset) => {
                if (!array.some(a => a.id === dataset.id)) {
                    array.push(dataset);
                }
                return array;
            }, SelectedDatasets);
        }

        setSelectedDatasets(SelectedDatasets);
    }

    const handleDatasetToggle = (e, dataset) => {
        const index = selectedDatasets.findIndex(d => d.id === dataset.id);
        let SelectedDatasets = [...selectedDatasets];

        if (index < 0) {
            SelectedDatasets.push(dataset);
        }
        else {
            SelectedDatasets.splice(index, 1);
        }

        setSelectedDatasets(SelectedDatasets);
    }

    const renderExamDataSelector = () => {
        const categories = Array.from(new Set(allDatasets.current.map(d => { return d.category; }))).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
        return categories.map((category, gi) => {
            const categoryDatasets = allDatasets.current.filter(d => d.category === category);
            const selectedCategoryDatasets = selectedDatasets.filter(s => categoryDatasets.some(c => c.id === s.id));
            const categoryKey = `exam_data_selector_category_${gi}`;
            const isExpanded = expandedCategories.some(c => c === category);
            return <Fragment
                key={categoryKey}
            >
                <div
                    className={'cursor-pointer border border-1 bg-gray-50 p-3' + (gi > 0 ? ' border-top-0' : '')}
                    onClick={(e) => { handleCategoryToggle(e, category) }}
                >
                    <div className='d-flex align-items-center w-100 text-gray-600'>
                        <div style={{ width: '8px' }}>
                            {
                                isExpanded ?
                                    <i className='fas fa-caret-down ml-nh'></i> :
                                    <i className='fas fa-caret-right'></i>
                            }
                        </div>
                        <strong className='flex-1 px-2'>{category}</strong>
                        <span className={'fs-75' + (selectedCategoryDatasets && selectedCategoryDatasets.length > 0 ? ' fw-500 text-info' : '')}>
                            ({(selectedCategoryDatasets && selectedCategoryDatasets.length > 0 ? selectedCategoryDatasets.length : 0)} / {categoryDatasets.length})
                        </span>
                    </div>
                </div>
                {
                    <div className={isExpanded ? '' : 'd-none'}>
                        <>
                            <div className='border border-1 border-top-0 p-2 bg-gray-50'>
                                <div className='d-flex align-items-center w-100'>
                                    <div className='custom-control custom-checkbox'>
                                        <input
                                            id={categoryKey}
                                            type='checkbox'
                                            name={categoryKey}
                                            className='custom-control-input'
                                            checked={isAllCategorySelected(category)}
                                            onChange={(e) => { handleCategorySelectAllToggle(e, category) }}
                                        />
                                        <label
                                            htmlFor={categoryKey}
                                            className='custom-control-label'
                                        >
                                            Select all
                                        </label>
                                    </div>
                                </div>
                            </div>
                            {
                                categoryDatasets.map((categoryDataset, ci) => {
                                    const categoryDatasetKey = `${categoryKey}_${ci}`;
                                    return <div
                                        key={categoryDatasetKey}
                                        className={'border border-1 border-top-0 p-2'}
                                    >
                                        <div className='d-flex align-items-center w-100 pl-2'>
                                            <div className='custom-control custom-checkbox'>
                                                <input
                                                    id={categoryDatasetKey}
                                                    type='checkbox'
                                                    name={categoryDatasetKey}
                                                    className='custom-control-input'
                                                    checked={selectedDatasets.some(d => d.id === categoryDataset.id)}
                                                    onChange={(e) => { handleDatasetToggle(e, categoryDataset) }}
                                                />
                                                <label
                                                    htmlFor={categoryDatasetKey}
                                                    className='custom-control-label'
                                                >
                                                    {categoryDataset.name}
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                })
                            }
                        </>
                    </div>
                }
            </Fragment>
        })
    }

    return <>
        <Observer>{() =>
            <>
                {
                    (props.drawer === quickDrawer.drawerOpened) ?
                        <GlobalHotKeys
                            keyMap={{
                                close: ['esc'],
                            }}
                            handlers={{
                                close: event => {
                                    handleCancel(event)
                                },
                            }}
                            allowChanges={true}
                        /> : null
                }
            </>
        }</Observer>
        <form ref={validateRef} onSubmit={handleSubmit}>
            <fieldset>
                <div className='add-exam-data-container quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('exam-dataset', 'default')}
                        action='Add'
                        category='Exam Data'
                        className='exam'
                        onCancel={handleCancel}
                    />
                    <div className='quick-drawer-body'>
                        <Observer>{() =>
                            <>
                                {
                                    isReady ?
                                        <FadeIn>
                                            <div className='body-content'>
                                                {
                                                    allDatasets.current && allDatasets.current.length > 0 ?
                                                        <section
                                                            className='m-0'
                                                        >
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='custom-control custom-checkbox mb-2'>
                                                                        <input
                                                                            id='exam_data_select_all'
                                                                            type='checkbox'
                                                                            name='exam_data_select_all'
                                                                            className='custom-control-input'
                                                                            checked={isAllSelected()}
                                                                            onChange={handleSelectAllToggle}
                                                                        />
                                                                        <label
                                                                            htmlFor='exam_data_select_all'
                                                                            className='custom-control-label'
                                                                        >
                                                                            Select all
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    {renderExamDataSelector()}
                                                                </div>
                                                            </div>
                                                        </section> : null
                                                }
                                            </div>
                                        </FadeIn> :
                                        <FadeIn>
                                            <div className='body-content'>
                                                <section>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className='text-muted'>No exam dataset found.</div>
                                                        </div>
                                                    </div>
                                                </section>
                                            </div>
                                        </FadeIn>
                                }
                            </>
                        }</Observer>
                    </div>
                    <div className='quick-drawer-action pl-3'>
                        <div className='row'>
                            <div className='col-12'>
                                <div className='float-right'>
                                    <button
                                        type='button'
                                        className='btn btn-link btn-cancel mr-2'
                                        onClick={handleCancel}
                                    >Cancel</button>
                                    <Observer>{() =>
                                        <button
                                            type='submit'
                                            className='btn btn-success'
                                        >Update</button>
                                    }</Observer>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
    </>
}

export default AddExamData;